import { useState, useRef } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { executeImportDirect } from '@timetable/api';
import { useAuthStore } from '@/store/authStore';
import type { ImportResult } from '@timetable/types';
import type { WizardState, WizardAction } from '../ImportWizard';

interface Step6Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  onBack: () => void;
  onStartNew: () => void;
}

export function Step6Confirm({ state, dispatch, onBack, onStartNew }: Step6Props) {
  const { user } = useAuthStore();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importingRef = useRef(false);

  const md = state.parsedMasterData;
  const summaryRows = [
    { entity: 'Teachers', count: md?.teachers.length ?? 0 },
    { entity: 'Subjects', count: md?.subjects.length ?? 0 },
    { entity: 'Classes', count: md?.classes.length ?? 0 },
    { entity: 'Rooms', count: md?.rooms?.length ?? 0 },
    { entity: 'Class Schedules', count: md?.schedules.length ?? 0 },
    { entity: 'Schedule Periods', count: md?.schedules.flatMap(s => s.periods).length ?? 0 },
    { entity: 'Schedule Assignments', count: md?.scheduleAssignments?.length ?? 0 },
    { entity: 'Teacher-Subjects', count: md?.teacherSubjects.length ?? 0 },
    { entity: 'Teacher-Class', count: md?.teacherClasses?.length ?? 0 },
    { entity: 'Subject-Class', count: md?.subjectClasses?.length ?? 0 },
    ...(state.includeTimetable
      ? [{ entity: 'Timetable Rows', count: state.parsedTimetableRows?.length ?? 0 }]
      : []),
  ];

  const handleConfirmImport = async () => {
    if (importingRef.current) return; // prevent double-fire race condition
    importingRef.current = true;
    setConfirmOpen(false);
    if (!state.session || !user?.id) { importingRef.current = false; return; }
    setImporting(true);
    setImportError(null);
    try {
      const result = await executeImportDirect(state.session.id, user.id);
      dispatch({ type: 'SET_IMPORT_RESULT', payload: result });
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
      importingRef.current = false;
    }
  };

  // Success screen
  if (state.importResult) {
    return <ImportSuccessScreen result={state.importResult} onStartNew={onStartNew} />;
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Step 6: Confirm &amp; Import</Typography>
      <Typography variant="body2" color="text.secondary">
        Review the summary below. Existing records matching the import keys will be updated.
      </Typography>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entity</TableCell>
              <TableCell align="right">Records to Upsert</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryRows.map((r) => (
              <TableRow key={r.entity}>
                <TableCell>{r.entity}</TableCell>
                <TableCell align="right">{r.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {importError && <Alert severity="error">{importError}</Alert>}

      {importing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">Importing data…</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack} disabled={importing}>Back</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={importing}
          onClick={() => setConfirmOpen(true)}
        >
          Process &amp; Import
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !importing && setConfirmOpen(false)}>
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will upsert all records listed above. Existing records matching the import keys
            will be replaced. This action cannot be undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={importing}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmImport} disabled={importing}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ImportSuccessScreen({
  result,
  onStartNew,
}: {
  result: ImportResult;
  onStartNew: () => void;
}) {
  const rows = [
    { entity: 'Teachers', ...result.teachers },
    { entity: 'Subjects', ...result.subjects },
    { entity: 'Classes', ...result.classes },
    { entity: 'Rooms', ...result.rooms },
    { entity: 'Class Schedules + Periods', ...result.schedules },
    { entity: 'Schedule Assignments', ...result.scheduleAssignments },
    { entity: 'Teacher-Subjects', ...result.teacherSubjects },
    { entity: 'Teacher-Class', ...result.teacherClasses },
    { entity: 'Subject-Class', ...result.subjectClasses },
    ...(result.timetableRecords
      ? [{ entity: 'Timetable Rows', ...result.timetableRecords }]
      : []),
  ];

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center' }}>
      <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mx: 'auto' }} />
      <Typography variant="h6">Import Complete</Typography>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entity</TableCell>
              <TableCell align="right">Inserted</TableCell>
              <TableCell align="right">Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.entity}>
                <TableCell>{r.entity}</TableCell>
                <TableCell align="right">{r.inserted}</TableCell>
                <TableCell align="right">{r.updated}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button variant="contained" onClick={onStartNew}>
        Start New Import
      </Button>
    </Box>
  );
}
